/**
 * FIFO client-side sync queue.
 *
 * Per design.md「關鍵流程：寫入交易（OCC）」 + spec "Client-Side Sync Queue
 * with Retry":
 *
 * - Single in-flight worker; runs ops serially. (Two concurrent writers
 *   would both read the same row at the same rev, both pass the OCC check
 *   in `occ.ts`, then race on the write — we'd silently lose data.)
 * - Persisted in localStorage under `mochia.sync_queue`. Only op shape +
 *   payload is stored. We never persist tokens, PII, or any auth material.
 * - Failure handling:
 *     - `OccConflictError` → notification was already written by `occ.ts`,
 *       so we just dequeue.
 *     - other errors → exponential backoff `1s, 4s, 16s` (per spec) up to
 *       3 retries; on the 4th failure write `sync_failed` notification +
 *       dequeue.
 * - When the queue drains from non-empty to empty, write
 *   `meta.last_sync_at = now()`.
 *
 * Payload validation: every entry persisted in localStorage flows through
 * `parseSyncOp` before being processed. Forged / malformed entries are
 * dropped at load time (see `proposal.md` "payload smuggling" sharp edge).
 * `enqueue` itself accepts a typed `SyncOp` discriminated union — callers
 * cannot mismatch op type and payload at compile time.
 */

import type { SheetsClient } from './client';
import { OccConflictError } from './occ';
import { notificationsRepo } from './repositories/notifications';
import { metaRepo } from './repositories/meta';

export const SYNC_QUEUE_STORAGE_KEY = 'mochia.sync_queue';
const MAX_RETRIES = 3;
// 1s, 4s, 16s — kept explicit so the backoff is auditable.
const BACKOFFS_MS = [1_000, 4_000, 16_000] as const;

/** All supported sync op types. Each type has exactly one variant in the
 *  `SyncOp` discriminated union with a fixed payload shape. */
export type SyncOpType =
  | 'create_tx'
  | 'update_tx'
  | 'delete_tx'
  | 'create_book'
  | 'update_book'
  | 'delete_book'
  | 'create_recurring'
  | 'update_recurring'
  | 'set_meta'
  | 'create_notification'
  | 'update_notification'
  // Legacy types retained for `SyncOpType` only — there is no `SyncOp`
  // discriminated variant for these, so `parseSyncOp` rejects them at
  // runtime. They exist to keep older UI labels (`CloudSyncScreen`)
  // compiling during the transition.
  | 'create_category'
  | 'update_category'
  | 'delete_category'
  | 'delete_recurring';

// --- Payload shapes per op variant -----------------------------------------
// Each shape is closed: `parseSyncOp` rejects payloads with extra keys to
// prevent payload smuggling (e.g. tacking on `deleted_at` to a benign update).

export type TxKindPayload = 'expense' | 'income' | 'transfer';
export type FrequencyPayload = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface CreateTxPayload {
  id: string;
  book_id: string;
  /** v5: optionally suffixed with `THH:MM`. See `lib/sheets/types.ts`
   *  IsoDate JSDoc. Pre-v5 queue entries with a separate `time` key are
   *  still accepted by the validator and folded into `date` here. */
  date: string;
  amount_minor: number;
  currency: string;
  kind: TxKindPayload;
  category_id: string;
  subcategory_id: string;
  note: string;
  /** @deprecated v3 schema. Kept on the wire so v2 → v3 migration windows
   *  don't reject old queue entries. New writes from the UI pass `''`. */
  project: string;
  /** Comma-joined tag string. Empty = no tags. See `lib/tags.ts`. */
  tags: string;
  recurring_id: string;
}

export interface UpdateTxPayload {
  id: string;
  rev: number;
  patch: Record<string, unknown>;
}

export interface DeleteByIdRevPayload {
  id: string;
  rev: number;
}

export interface CreateBookPayload {
  id: string;
  name: string;
  color_token: string;
  /** v3: typed book. 'personal' | 'family' | 'project'. */
  type: 'personal' | 'family' | 'project';
  is_default: boolean;
}

export interface UpdateBookPayload {
  id: string;
  rev: number;
  patch: Record<string, unknown>;
}

export interface CreateCategoryPayload {
  id: string;
  /** v3: book this category lives in. Empty string = system seed (visible
   *  in every book). NOTE: this is the *book* id; do not confuse with
   *  `parent_id`, which is the parent *category* id for sub-categories.
   *  Both happen to be string-typed so swapping them is silently legal at
   *  compile time — branding to nominal types is a follow-up. */
  book_id: string;
  parent_id: string;
  name: string;
  kind: 'expense' | 'income';
  color_token: string;
  icon: string;
  sort_order: number;
  is_system: boolean;
}

export interface UpdateCategoryPayload {
  id: string;
  rev: number;
  patch: Record<string, unknown>;
}

export interface CreateRecurringPayload {
  id: string;
  book_id: string;
  title: string;
  amount_minor: number;
  currency: string;
  kind: TxKindPayload;
  category_id: string;
  subcategory_id: string;
  note: string;
  freq: FrequencyPayload;
  freq_day: number;
  start_date: string;
  end_date: string;
  last_generated_date: string;
  active: boolean;
}

export interface UpdateRecurringPayload {
  id: string;
  rev: number;
  patch: Record<string, unknown>;
}

export interface SetMetaPayload {
  /** MUST be one of `MetaKey`. We re-declare as a string-typed field here
   *  rather than importing `MetaKey` to keep `parseSyncOp` independent of
   *  the type evolution; runtime validation in `validatePayload(set_meta)`
   *  + the strict `metaRepo.setValue` signature guarantee that anything
   *  mismatched gets caught either at compile or queue-load time. */
  key: string;
  value: string;
}

export interface CreateNotificationPayload {
  kind: 'occ_conflict' | 'recurring_generated' | 'sync_failed' | 'system';
  payload: Record<string, unknown>;
  /** Optional pre-generated id. Lets the scheduler do an optimistic
   *  local insert under the same id so reconciliation on the next
   *  reload doesn't surface a duplicate notification. */
  id?: string;
  /** Optional pre-generated created_at — matches the optimistic local
   *  row so post-reload state is byte-identical. */
  created_at?: string;
}

export interface UpdateNotificationPayload {
  id: string;
  read_at: string;
}

interface OpEnvelope {
  id: string;
  attempts: number;
  last_error: string;
  created_at: string;
}

/**
 * Loose envelope shape for callers that have not yet migrated to the
 * discriminated `SyncOp` union (UI components, scheduler). New code SHOULD
 * use `SyncOpInput` (queue) or the strict `SyncOp` (this file). The slice
 * layer accepts this shape so existing UI enqueues compile during the
 * transition; the runtime queue still validates via `parseSyncOp`.
 *
 * Kept here so `state/slices/sync.ts` can import from the same module
 * (avoids duplication) without forcing the whole UI to refactor.
 */
export interface LegacySyncOp<P = unknown> {
  id: string;
  type: SyncOpType;
  payload: P;
  attempts: number;
  last_error: string;
  created_at: string;
}

export type SyncOp =
  | (OpEnvelope & { type: 'create_tx'; payload: CreateTxPayload })
  | (OpEnvelope & { type: 'update_tx'; payload: UpdateTxPayload })
  | (OpEnvelope & { type: 'delete_tx'; payload: DeleteByIdRevPayload })
  | (OpEnvelope & { type: 'create_book'; payload: CreateBookPayload })
  | (OpEnvelope & { type: 'update_book'; payload: UpdateBookPayload })
  | (OpEnvelope & { type: 'delete_book'; payload: DeleteByIdRevPayload })
  | (OpEnvelope & { type: 'create_category'; payload: CreateCategoryPayload })
  | (OpEnvelope & { type: 'update_category'; payload: UpdateCategoryPayload })
  | (OpEnvelope & { type: 'delete_category'; payload: DeleteByIdRevPayload })
  | (OpEnvelope & { type: 'create_recurring'; payload: CreateRecurringPayload })
  | (OpEnvelope & { type: 'update_recurring'; payload: UpdateRecurringPayload })
  | (OpEnvelope & { type: 'set_meta'; payload: SetMetaPayload })
  | (OpEnvelope & { type: 'create_notification'; payload: CreateNotificationPayload })
  | (OpEnvelope & { type: 'update_notification'; payload: UpdateNotificationPayload });

export type SyncOpHandler = (payload: unknown) => Promise<void>;

export interface SyncQueueOptions {
  spreadsheetId: string;
  handlers: Partial<Record<SyncOpType, SyncOpHandler>>;
  /** Sheets client used by repos for notifications/meta writes. Optional in
   *  tests, where the repo modules are mocked. */
  client?: SheetsClient;
  /** Override storage (used in tests; defaults to localStorage). */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
  /** Override the timer (used in tests). Defaults to setTimeout/clearTimeout. */
  setTimeout?: typeof setTimeout;
  clearTimeout?: typeof clearTimeout;
  /** Hook for sync slice integration. */
  onChange?: (snapshot: SyncQueueSnapshot) => void;
}

export interface SyncQueueSnapshot {
  pending: ReadonlyArray<SyncOp>;
  inflight: SyncOp | null;
  lastSyncAt: string | null;
}

/** Input shape for `enqueue`: a typed `SyncOp` minus the envelope fields
 *  the queue stamps itself (id, attempts, last_error, created_at). */
export type SyncOpInput =
  | { type: 'create_tx'; payload: CreateTxPayload }
  | { type: 'update_tx'; payload: UpdateTxPayload }
  | { type: 'delete_tx'; payload: DeleteByIdRevPayload }
  | { type: 'create_book'; payload: CreateBookPayload }
  | { type: 'update_book'; payload: UpdateBookPayload }
  | { type: 'delete_book'; payload: DeleteByIdRevPayload }
  | { type: 'create_category'; payload: CreateCategoryPayload }
  | { type: 'update_category'; payload: UpdateCategoryPayload }
  | { type: 'delete_category'; payload: DeleteByIdRevPayload }
  | { type: 'create_recurring'; payload: CreateRecurringPayload }
  | { type: 'update_recurring'; payload: UpdateRecurringPayload }
  | { type: 'set_meta'; payload: SetMetaPayload }
  | { type: 'create_notification'; payload: CreateNotificationPayload }
  | { type: 'update_notification'; payload: UpdateNotificationPayload };

export interface SyncQueue {
  enqueue(op: SyncOpInput): SyncOp;
  size(): number;
  drain(): Promise<void>;
  snapshot(): SyncQueueSnapshot;
  clear(): void;
}

function genOpId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

// --- parseSyncOp -----------------------------------------------------------

const ENVELOPE_KEYS: ReadonlySet<string> = new Set([
  'id',
  'type',
  'payload',
  'attempts',
  'last_error',
  'created_at',
]);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function hasOnlyKeys(o: Record<string, unknown>, allowed: ReadonlySet<string>): boolean {
  for (const k of Object.keys(o)) {
    if (!allowed.has(k)) return false;
  }
  return true;
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}

function isNonNegInt(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

function isBoolean(v: unknown): v is boolean {
  return typeof v === 'boolean';
}

function validatePayload(type: SyncOpType, payload: unknown): boolean {
  if (!isPlainObject(payload)) return false;
  switch (type) {
    case 'create_tx': {
      // `time` is grandfathered: v4 queued ops with a separate `time`
      // key still validate, but new writes fold time into `date`.
      const allowed = new Set([
        'id', 'book_id', 'date', 'time', 'amount_minor', 'currency', 'kind',
        'category_id', 'subcategory_id', 'note', 'project', 'tags', 'recurring_id',
      ]);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['id']) || !isString(payload['book_id'])) return false;
      if (!isString(payload['date']) || !isString(payload['currency'])) return false;
      if (!isString(payload['category_id']) || !isString(payload['subcategory_id'])) return false;
      if (!isString(payload['note']) || !isString(payload['project'])) return false;
      if (!isString(payload['tags'])) return false;
      if (!isString(payload['recurring_id'])) return false;
      if (payload['time'] !== undefined && !isString(payload['time'])) return false;
      const amt = payload['amount_minor'];
      if (typeof amt !== 'number' || !Number.isInteger(amt)) return false;
      const kind = payload['kind'];
      if (kind !== 'expense' && kind !== 'income' && kind !== 'transfer') return false;
      return true;
    }
    case 'update_tx':
    case 'update_book':
    case 'update_category':
    case 'update_recurring': {
      const allowed = new Set(['id', 'rev', 'patch']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['id'])) return false;
      if (!isNonNegInt(payload['rev'])) return false;
      if (!isPlainObject(payload['patch'])) return false;
      return true;
    }
    case 'delete_tx':
    case 'delete_book':
    case 'delete_category': {
      const allowed = new Set(['id', 'rev']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['id'])) return false;
      if (!isNonNegInt(payload['rev'])) return false;
      return true;
    }
    case 'create_book': {
      const allowed = new Set(['id', 'name', 'color_token', 'type', 'is_default']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['id']) || !isString(payload['name'])) return false;
      if (!isString(payload['color_token'])) return false;
      const t = payload['type'];
      if (t !== 'personal' && t !== 'family' && t !== 'project') return false;
      if (!isBoolean(payload['is_default'])) return false;
      return true;
    }
    case 'create_category': {
      const allowed = new Set([
        'id', 'book_id', 'parent_id', 'name', 'kind', 'color_token', 'icon', 'sort_order', 'is_system',
      ]);
      if (!hasOnlyKeys(payload, allowed)) return false;
      for (const k of ['id', 'book_id', 'parent_id', 'name', 'color_token', 'icon']) {
        if (!isString(payload[k])) return false;
      }
      const kind = payload['kind'];
      if (kind !== 'expense' && kind !== 'income') return false;
      const so = payload['sort_order'];
      if (typeof so !== 'number' || !Number.isInteger(so)) return false;
      if (!isBoolean(payload['is_system'])) return false;
      return true;
    }
    case 'create_recurring': {
      const allowed = new Set([
        'id', 'book_id', 'title', 'amount_minor', 'currency', 'kind',
        'category_id', 'subcategory_id', 'note',
        'freq', 'freq_day', 'start_date', 'end_date', 'last_generated_date',
        'active',
      ]);
      if (!hasOnlyKeys(payload, allowed)) return false;
      for (const k of [
        'id', 'book_id', 'title', 'currency', 'category_id', 'subcategory_id',
        'note', 'start_date', 'end_date', 'last_generated_date',
      ]) {
        if (!isString(payload[k])) return false;
      }
      const amt = payload['amount_minor'];
      if (typeof amt !== 'number' || !Number.isInteger(amt)) return false;
      const kind = payload['kind'];
      if (kind !== 'expense' && kind !== 'income' && kind !== 'transfer') return false;
      const freq = payload['freq'];
      if (freq !== 'daily' && freq !== 'weekly' && freq !== 'monthly' && freq !== 'yearly') return false;
      const freqDay = payload['freq_day'];
      if (typeof freqDay !== 'number' || !Number.isInteger(freqDay)) return false;
      if (!isBoolean(payload['active'])) return false;
      return true;
    }
    case 'set_meta': {
      const allowed = new Set(['key', 'value']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['key']) || !isString(payload['value'])) return false;
      return true;
    }
    case 'create_notification': {
      // `id` + `created_at` are optional — the scheduler passes both so
      // its optimistic local insert and the eventual sheet row share an
      // identifier (no duplicate after reload). Older queue entries
      // without them still validate.
      const allowed = new Set(['kind', 'payload', 'id', 'created_at']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      const kind = payload['kind'];
      if (
        kind !== 'occ_conflict' &&
        kind !== 'recurring_generated' &&
        kind !== 'sync_failed' &&
        kind !== 'system'
      ) {
        return false;
      }
      if (!isPlainObject(payload['payload'])) return false;
      if (payload['id'] !== undefined && !isString(payload['id'])) return false;
      if (payload['created_at'] !== undefined && !isString(payload['created_at'])) return false;
      return true;
    }
    case 'update_notification': {
      const allowed = new Set(['id', 'read_at']);
      if (!hasOnlyKeys(payload, allowed)) return false;
      if (!isString(payload['id']) || !isString(payload['read_at'])) return false;
      return true;
    }
    default:
      return false;
  }
}

const KNOWN_TYPES: ReadonlySet<SyncOpType> = new Set([
  'create_tx', 'update_tx', 'delete_tx',
  'create_book', 'update_book', 'delete_book',
  'create_category', 'update_category', 'delete_category',
  'create_recurring', 'update_recurring',
  'set_meta', 'create_notification', 'update_notification',
]);

/**
 * Runtime validator for sync ops loaded from untrusted storage.
 *
 * Returns a typed `SyncOp` on success, or `null` on any failure (unknown
 * type, missing/extra keys, wrong field types). Callers MUST drop entries
 * that return `null` — proceeding with a partial shape is exactly the
 * payload-smuggling vector this validator exists to close.
 */
export function parseSyncOp(raw: unknown): SyncOp | null {
  if (!isPlainObject(raw)) return null;
  if (!hasOnlyKeys(raw, ENVELOPE_KEYS)) return null;

  const type = raw['type'];
  if (typeof type !== 'string' || !KNOWN_TYPES.has(type as SyncOpType)) return null;

  const id = raw['id'];
  if (typeof id !== 'string' || id === '') return null;

  const attempts = raw['attempts'];
  if (typeof attempts !== 'number' || !Number.isInteger(attempts) || attempts < 0) return null;

  const last_error = raw['last_error'];
  if (typeof last_error !== 'string') return null;

  const created_at = raw['created_at'];
  if (typeof created_at !== 'string') return null;

  if (!validatePayload(type as SyncOpType, raw['payload'])) return null;

  // Cast safe — `validatePayload` narrowed payload structure for `type`.
  return {
    id,
    type: type as SyncOpType,
    payload: raw['payload'],
    attempts,
    last_error,
    created_at,
  } as SyncOp;
}

// --- queue impl ------------------------------------------------------------

export function createSyncQueue(options: SyncQueueOptions): SyncQueue {
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  const setTo = options.setTimeout ?? setTimeout;
  const clearTo = options.clearTimeout ?? clearTimeout;
  const handlers = options.handlers;

  let queue: SyncOp[] = loadFromStorage(storage);
  let inflight: SyncOp | null = null;
  let lastSyncAt: string | null = null;
  let processing = false;
  let drainResolvers: Array<() => void> = [];

  function persist(): void {
    if (!storage) return;
    try {
      if (queue.length === 0) {
        storage.removeItem(SYNC_QUEUE_STORAGE_KEY);
      } else {
        storage.setItem(SYNC_QUEUE_STORAGE_KEY, JSON.stringify(queue));
      }
    } catch {
      /* quota or disabled — sync queue degrades to in-memory only */
    }
  }

  function snapshot(): SyncQueueSnapshot {
    return { pending: [...queue], inflight, lastSyncAt };
  }

  function emit(): void {
    options.onChange?.(snapshot());
  }

  function enqueue(input: SyncOpInput): SyncOp {
    const op = {
      id: genOpId(),
      type: input.type,
      payload: input.payload,
      attempts: 0,
      last_error: '',
      created_at: new Date().toISOString(),
    } as SyncOp;
    queue.push(op);
    persist();
    emit();
    void process();
    return op;
  }

  async function process(): Promise<void> {
    if (processing) return;
    processing = true;
    try {
      while (queue.length > 0) {
        const op = queue[0]!;
        inflight = op;
        emit();
        const handler = handlers[op.type];
        if (!handler) {
          // No handler registered — write sync_failed and skip.
          await safeNotifyFailure(op, `No handler for op type: ${op.type}`);
          queue.shift();
          inflight = null;
          persist();
          emit();
          continue;
        }

        let succeeded = false;
        let dequeueImmediately = false;
        let lastError: unknown = null;

        // attempt 0 = immediate; subsequent retries follow BACKOFFS_MS.
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            const delayIdx = attempt - 1;
            const delay = BACKOFFS_MS[delayIdx] ?? BACKOFFS_MS[BACKOFFS_MS.length - 1]!;
            await wait(delay, setTo, clearTo);
          }
          op.attempts = attempt + 1;
          try {
            await handler(op.payload);
            succeeded = true;
            break;
          } catch (err) {
            lastError = err;
            op.last_error = err instanceof Error ? err.message : String(err);
            // OCC conflict → notification already appended by occ.ts.
            // Dequeue without further retries and without writing sync_failed.
            if (err instanceof OccConflictError) {
              dequeueImmediately = true;
              break;
            }
            // otherwise loop to next retry (or exit when attempts exhausted)
          }
        }

        if (!succeeded && !dequeueImmediately) {
          await safeNotifyFailure(op, formatError(lastError));
        }

        // Either way, dequeue.
        queue.shift();
        inflight = null;
        persist();
        emit();
      }

      // Drained → update last_sync_at
      if (queue.length === 0) {
        const now = new Date().toISOString();
        try {
          await metaRepo.setValue(repoCtx(), 'last_sync_at', now);
        } catch {
          /* don't crash the worker if meta write fails */
        }
        lastSyncAt = now;
        emit();
      }
    } finally {
      processing = false;
      const resolvers = drainResolvers;
      drainResolvers = [];
      for (const r of resolvers) r();
    }
  }

  function repoCtx(): { client: SheetsClient; spreadsheetId: string } {
    // In tests the repo modules are mocked, so we never touch the actual
    // client — pass a stub. In production runtime the caller wires a real
    // SheetsClient via `options.client`.
    return {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      client: (options.client ?? (undefined as any)) as SheetsClient,
      spreadsheetId: options.spreadsheetId,
    };
  }

  async function safeNotifyFailure(op: SyncOp, error: string): Promise<void> {
    try {
      await notificationsRepo.append(repoCtx(), {
        kind: 'sync_failed',
        payload: { op: op.type, op_id: op.id, error },
      });
    } catch {
      /* swallow — we don't have a tertiary channel */
    }
  }

  function drain(): Promise<void> {
    if (queue.length === 0 && !processing) return Promise.resolve();
    return new Promise<void>((resolve) => {
      drainResolvers.push(resolve);
      void process();
    });
  }

  function clear(): void {
    queue = [];
    persist();
    emit();
  }

  return {
    enqueue,
    size: () => queue.length,
    drain,
    snapshot,
    clear,
  };
}

function loadFromStorage(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
): SyncOp[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(SYNC_QUEUE_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: SyncOp[] = [];
    for (const entry of parsed) {
      const op = parseSyncOp(entry);
      if (op) {
        out.push(op);
      } else {
        // eslint-disable-next-line no-console
        console.warn('Dropping malformed sync op', {
          id: isPlainObject(entry) && typeof entry['id'] === 'string' ? entry['id'] : '<unknown>',
        });
      }
    }
    return out;
  } catch {
    return [];
  }
}

function formatError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err == null) return 'Unknown error';
  return String(err);
}

function wait(
  ms: number,
  setTo: typeof setTimeout,
  _clearTo: typeof clearTimeout,
): Promise<void> {
  return new Promise<void>((resolve) => {
    setTo(() => resolve(), ms);
  });
}
